import { getOfflineDb, getOrCreateDeviceId, type PosSyncMode } from './db';
import { useSyncStore } from '@/store/syncStore';

export { resolveEffectivePosSyncMode } from './syncModePolicy';

export function getCompanyCode(): string {
  return localStorage.getItem('company_code') || 'default';
}

export function companyDb() {
  return getOfflineDb(getCompanyCode());
}

export async function ensureDeviceId(): Promise<string> {
  const db = companyDb();
  const deviceId = getOrCreateDeviceId();
  await db.meta.put({ key: 'deviceId', value: deviceId });
  return deviceId;
}

export function isLocalFirstMode(mode?: PosSyncMode): boolean {
  const m = mode ?? useSyncStore.getState().posSyncMode;
  return m === 'Local First Auto Sync' || m === 'Offline Always Manual Sync';
}

export function newOfflineId(prefix = 'local'): string {
  return `${prefix}_${crypto.randomUUID()}`;
}

export async function getMeta<T = unknown>(key: string): Promise<T | null> {
  const row = await companyDb().meta.get(key);
  return (row?.value as T) ?? null;
}

export async function setMeta(key: string, value: unknown): Promise<void> {
  await companyDb().meta.put({ key, value });
}

export async function getIdMaps(): Promise<Record<string, string>> {
  return (await getMeta<Record<string, string>>('idMaps')) || {};
}

export async function mapLocalToServer(localId: string, serverName: string): Promise<void> {
  const maps = await getIdMaps();
  maps[localId] = serverName;
  await setMeta('idMaps', maps);
}

export async function resolveServerId(id: string | undefined | null): Promise<string | undefined> {
  if (!id) return undefined;
  if (!id.startsWith('local_')) return id;
  const maps = await getIdMaps();
  return maps[id] || id;
}

export async function resolvePayloadIds<T extends Record<string, unknown>>(
  payload: T,
  keys: string[],
): Promise<T> {
  const out = { ...payload };
  for (const key of keys) {
    const val = out[key];
    if (typeof val === 'string') {
      (out as Record<string, unknown>)[key] = (await resolveServerId(val)) || val;
    }
  }
  return out;
}
